import * as Sentry from '@sentry/react';
import { isRunningLocally } from '../config/env';

export interface Logger {
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, error?: unknown) => void;
  info: (message: string, ...args: unknown[]) => void;
  debug: (message: string, ...args: unknown[]) => void;
}

/**
 * Centralized logging service that integrates with Sentry
 * Provides consistent logging interface across the application
 */
class LoggerService implements Logger {
  private context: string;

  constructor(context: string = 'Application') {
    this.context = context;
  }

  /**
   * Log a warning message
   */
  warn(message: string, ...args: unknown[]): void {
    const formattedMessage = `[${this.context}] ${message}`;

    // Always log to console
    console.warn(formattedMessage, ...args);

    // Send to Sentry in non-local environments
    if (!isRunningLocally()) {
      Sentry.addBreadcrumb({
        category: 'warning',
        message: formattedMessage,
        data: args.length > 0 ? args : undefined,
        level: 'warning'
      });
    }
  }

  /**
   * Log an error message
   */
  error(message: string, error?: unknown): void {
    const formattedMessage = `[${this.context}] ${message}`;

    // Always log to console
    console.error(formattedMessage, error);

    // Send to Sentry in non-local environments
    if (!isRunningLocally()) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { context: this.context },
          extra: { message: formattedMessage }
        });
      } else {
        Sentry.captureMessage(formattedMessage, {
          level: 'error',
          tags: { context: this.context },
          extra: { error }
        });
      }
    }
  }

  /**
   * Log an info message
   */
  info(message: string, ...args: unknown[]): void {
    const formattedMessage = `[${this.context}] ${message}`;

    // Always log to console
    console.info(formattedMessage, ...args);

    // Send to Sentry in non-local environments
    if (!isRunningLocally()) {
      Sentry.addBreadcrumb({
        category: 'info',
        message: formattedMessage,
        data: args.length > 0 ? args : undefined,
        level: 'info'
      });
    }
  }

  /**
   * Log a debug message (only in development)
   */
  debug(message: string, ...args: unknown[]): void {
    // Only log debug messages in development
    if (process.env.NODE_ENV === 'development') {
      const formattedMessage = `[${this.context}] ${message}`;
      console.debug(formattedMessage, ...args);
    }
  }

  /**
   * Create a new logger instance with a specific context
   */
  static create(context: string): Logger {
    return new LoggerService(context);
  }
}

// Default logger instance
const defaultLogger = new LoggerService();

export { defaultLogger, LoggerService };
export default defaultLogger;
